import { useEffect } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import type { OutreachAllowed, OutreachLevel, RequestOutreach, ShiftRequest } from "../utils/models";
import { useDemoRequestOutreach } from "./useDemoRequestOutreach";

/** A choice from the Adjust plan panel. Changes this shift only. */
export type OutreachPlanChoice = {
	level: OutreachLevel;
	/** Stay on `level` instead of stepping up on schedule. Stepping below our pick always holds. */
	holding: boolean;
	allowed: OutreachAllowed;
};

/** What a user can do to a request's outreach. Each one changes who hears from us, when, or who gets on the shift. */
export interface RequestOutreachActions {
	pause: () => void;
	resume: () => void;
	textNow: (nurseId: number) => void;
	/** Calls someone on the call list now, ahead of their turn. */
	callNow: (nurseId: number) => void;
	skip: (nurseId: number) => void;
	undoSkip: (nurseId: number) => void;
	/** Sends to someone an overridable filter left out. */
	textAnyway: (nurseId: number) => void;
	adjustPlan: (choice: OutreachPlanChoice) => void;
	/** Back to our plan: the level the clock gives, with calls, overtime and the agency allowed. */
	resetPlan: () => void;
	/** Puts a waiting sign-up on the shift. */
	approve: (nurseId: number) => void;
	/** Turns a waiting sign-up down; outreach keeps looking. */
	decline: (nurseId: number) => void;
}

export enum RequestOutreachStatus {
	Ready = "ready",
	/** This department doesn't get the outreach view yet; callers render what they did before. */
	Unavailable = "unavailable",
}

export type RequestOutreachResult =
	| { status: RequestOutreachStatus.Ready; outreach: RequestOutreach; actions: RequestOutreachActions }
	| { status: RequestOutreachStatus.Unavailable };

/** Whether the department gets the "Who we're asking" outreach view. Demo departments only, until APP-918. */
export const useHasRequestOutreach = (): boolean => useHospitalData().isRealDemo;

/**
 * Who a request's outreach has reached, who is next and who is left out, and the actions on it. Demo departments
 * get it from seeded mock data built on the real request and roster (`useDemoRequestOutreach`, local state only);
 * every other department reads `Unavailable`. APP-918 swaps the demo source for the backend.
 */
export const useRequestOutreach = (request: ShiftRequest): RequestOutreachResult => {
	const { trackError } = useAnalytics();
	const isAvailable = useHasRequestOutreach();
	const demo = useDemoRequestOutreach(request, isAvailable);

	useEffect(() => {
		if (isAvailable && demo === null) trackError("request_outreach_load", { request_id: request.id });
	}, [isAvailable, demo, request.id, trackError]);

	if (demo === null) return { status: RequestOutreachStatus.Unavailable };
	return { status: RequestOutreachStatus.Ready, outreach: demo.outreach, actions: demo.actions };
};
